/**
 * Download Banner
 * Subtle bottom-right floating button that links to the native app download.
 * Auto-detects OS and shows the right download link.
 * Only renders when isPWA() — hidden on Electron and Capacitor.
 */

import { useState } from 'react';
import { isPWA } from '@/services/platform';

const DOWNLOAD_URLS: Record<string, string> = {
  mac: 'https://highfiveupdates.super5.space/desktop/mac/HighFive-latest-arm64-mac.zip',
  windows: 'https://highfiveupdates.super5.space/desktop/win/HighFive-latest.exe',
  android: 'https://highfiveupdates.super5.space/mobile/HighFive-latest.apk',
};

const OS_LABELS: Record<string, string> = {
  mac: 'macOS',
  windows: 'Windows',
  android: 'Android',
};

function getOS(): string {
  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return 'android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'ios';
  if (/Mac/i.test(ua)) return 'mac';
  if (/Win/i.test(ua)) return 'windows';
  return 'unknown';
}

export const DownloadBanner = () => {
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem('highfive_download_dismissed') === 'true'
  );
  const [os] = useState(() => getOS());

  if (!isPWA() || dismissed) return null;

  const downloadUrl = DOWNLOAD_URLS[os];
  const label = OS_LABELS[os];

  // No download available for this OS (iOS, Linux, unknown)
  if (!downloadUrl) return null;

  const handleDismiss = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    localStorage.setItem('highfive_download_dismissed', 'true');
    setDismissed(true);
  };

  return (
    <a
      href={downloadUrl}
      className="fixed bottom-4 right-4 z-40 flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white dark:bg-dark-bg-elevated text-gray-700 dark:text-dark-text-primary text-xs font-medium shadow-lg border border-gray-200/60 dark:border-dark-border-secondary backdrop-blur-sm hover:shadow-xl hover:border-primary-200 dark:hover:border-primary-500/30 transition-all group animate-fade-in"
      title={`Изтегли за ${label}`}
    >
      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
      <span>Изтегли за {label}</span>
      <button
        onClick={handleDismiss}
        className="ml-1 p-0.5 rounded-full hover:bg-white/20 dark:hover:bg-black/20 transition-colors"
        title="Скрий"
      >
        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>
    </a>
  );
};
